// src/components/TopBar/TopBar.tsx
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../Button/Button';
import Typography from '../Text/Typography';
import MobileMenuButton from '../MobileMenuButton/MobileMenuButton';
import MobileMenu from '../MobileMenu/MobileMenu';
import Popover from '../Popover/Popover';
import ResponsiveDialog from '../ResponsiveDialog/ResponsiveDialog';
import './Topbar.css';

const Topbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStatus, user, signOut, login, signup } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isDesktopUserPopoverOpen, setIsDesktopUserPopoverOpen] = useState(false);
  const [isMobileUserPopoverOpen, setIsMobileUserPopoverOpen] = useState(false);
  const [isActiveIngredientsDialogOpen, setIsActiveIngredientsDialogOpen] = useState(false);
  const [isActiveDetailDialogOpen, setIsActiveDetailDialogOpen] = useState(false);
  const [selectedActiveId, setSelectedActiveId] = useState<string | null>(null);
  const desktopUserAvatarRef = useRef<HTMLDivElement>(null);
  const mobileUserAvatarRef = useRef<HTMLDivElement>(null);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleDesktopUserAvatarClick = () => {
    setIsDesktopUserPopoverOpen(!isDesktopUserPopoverOpen);
  };

  const handleMobileUserAvatarClick = () => {
    setIsMobileUserPopoverOpen(!isMobileUserPopoverOpen);
  };

  const handleLogout = async () => {
    try {
              await signOut();
      setIsDesktopUserPopoverOpen(false);
      setIsMobileUserPopoverOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMyProfile = () => {
    navigate('/settings');
    setIsDesktopUserPopoverOpen(false);
    setIsMobileUserPopoverOpen(false);
  };

  const handleActiveIngredients = () => {
    setIsActiveIngredientsDialogOpen(true);
    setIsDesktopUserPopoverOpen(false);
    setIsMobileUserPopoverOpen(false);
  };

  const handleActiveClick = (active: any) => {
    setSelectedActiveId(active.active_id);
    setIsActiveIngredientsDialogOpen(false);
    setIsActiveDetailDialogOpen(true);
  };

  const handleBackToActiveIngredients = () => {
    setIsActiveDetailDialogOpen(false);
    setIsActiveIngredientsDialogOpen(true);
  };

  const handleCloseActiveDetail = () => {
    setIsActiveDetailDialogOpen(false);
    setSelectedActiveId(null);
  };

  const userPopoverOptions = [
    {
      id: 'profile',
      label: 'My Profile',
      onClick: handleMyProfile
    },
    {
      id: 'about',
      label: 'About',
      onClick: () => {
        navigate('/learn/about');
        setIsDesktopUserPopoverOpen(false);
        setIsMobileUserPopoverOpen(false);
      }
    },
    {
      id: 'contact',
      label: 'Contact',
      onClick: () => {
        navigate('/help');
        setIsDesktopUserPopoverOpen(false);
        setIsMobileUserPopoverOpen(false);
      }
    },
    {
      id: 'logout',
      label: 'Logout',
      onClick: handleLogout
    }
  ];

  // Get user info from auth context
  const userInitials = user?.name 
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
    : user?.email 
    ? user.email.substring(0, 2).toUpperCase()
    : 'U';

  const isAuthenticated = authStatus === 'authenticated' && user;

  // Homepage greeting lives in the topbar so the dial owns the whole stage.
  const greeting = useMemo(() => {
    if (location.pathname !== '/') return null;

    const now = new Date();
    const hour = now.getHours();
    const timeOfDay =
      hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

    const rawName = user?.name || user?.email?.split('@')[0] || '';
    const firstName = rawName.includes('.')
      ? rawName.split('.')[0]
      : rawName.split(' ')[0];

    return {
      message: isAuthenticated && firstName ? `${timeOfDay}, ${firstName}` : timeOfDay,
      date: now.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }),
    };
  }, [location.pathname, isAuthenticated, user]);

  return (
    <>
      <section className="top-bar">
        <div className="top-bar-inner">
        <div className="top-bar-left">
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
            <svg
              width="69"
              height="28"
              viewBox="0 0 69 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="topbar-logo"
            >
              <path d="M15.9586 -2.14577e-06H18.9826V27H15.9586V23.94C15.3466 24.996 14.5246 25.848 13.4926 26.496C12.4606 27.144 11.2486 27.468 9.85659 27.468C8.54859 27.468 7.32459 27.222 6.18459 26.73C5.04459 26.238 4.03659 25.56 3.16059 24.696C2.29659 23.82 1.61859 22.812 1.12659 21.672C0.634594 20.532 0.388594 19.308 0.388594 18C0.388594 16.692 0.634594 15.468 1.12659 14.328C1.61859 13.176 2.29659 12.168 3.16059 11.304C4.03659 10.428 5.04459 9.744 6.18459 9.252C7.32459 8.76 8.54859 8.514 9.85659 8.514C11.2486 8.514 12.4606 8.838 13.4926 9.486C14.5246 10.134 15.3466 10.986 15.9586 12.042V-2.14577e-06ZM9.89259 24.534C11.0806 24.534 12.1066 24.246 12.9706 23.67C13.8466 23.082 14.5186 22.29 14.9866 21.294C15.4666 20.298 15.7066 19.2 15.7066 18C15.7066 16.776 15.4666 15.672 14.9866 14.688C14.5066 13.692 13.8286 12.906 12.9526 12.33C12.0886 11.742 11.0686 11.448 9.89259 11.448C8.70459 11.448 7.62459 11.742 6.65259 12.33C5.69259 12.918 4.92459 13.71 4.34859 14.706C3.77259 15.69 3.48459 16.788 3.48459 18C3.48459 19.212 3.77859 20.316 4.36659 21.312C4.95459 22.296 5.73459 23.082 6.70659 23.67C7.67859 24.246 8.74059 24.534 9.89259 24.534ZM31.0448 27.468C29.3888 27.468 27.8768 27.042 26.5088 26.19C25.1528 25.338 24.0668 24.198 23.2508 22.77C22.4468 21.33 22.0448 19.74 22.0448 18C22.0448 16.68 22.2788 15.45 22.7468 14.31C23.2148 13.158 23.8568 12.15 24.6728 11.286C25.5008 10.41 26.4608 9.726 27.5528 9.234C28.6448 8.742 29.8088 8.496 31.0448 8.496C32.4008 8.496 33.6428 8.772 34.7708 9.324C35.9108 9.864 36.8828 10.62 37.6868 11.592C38.5028 12.564 39.1088 13.692 39.5048 14.976C39.9128 16.26 40.0628 17.634 39.9548 19.098H25.2668C25.4108 20.118 25.7468 21.03 26.2748 21.834C26.8028 22.626 27.4748 23.256 28.2908 23.724C29.1188 24.192 30.0368 24.432 31.0448 24.444C32.1248 24.444 33.1028 24.168 33.9788 23.616C34.8548 23.052 35.5628 22.284 36.1028 21.312L39.1808 22.032C38.4608 23.628 37.3748 24.936 35.9228 25.956C34.4708 26.964 32.8448 27.468 31.0448 27.468ZM25.1768 16.722H36.9128C36.8168 15.714 36.4928 14.796 35.9408 13.968C35.3888 13.128 34.6868 12.456 33.8348 11.952C32.9828 11.448 32.0528 11.196 31.0448 11.196C30.0368 11.196 29.1128 11.442 28.2728 11.934C27.4328 12.426 26.7368 13.092 26.1848 13.932C25.6448 14.76 25.3088 15.69 25.1768 16.722ZM50.2247 27H47.1647L41.1347 9H44.0867L48.7307 22.734L53.3207 9H56.4347L61.0427 22.734L65.6327 9H68.6207L62.5907 27H59.5127L54.8507 13.14L50.2247 27Z" fill="currentColor"/>
            </svg>
          </span>
          <nav className="top-bar-nav" aria-label="Primary">
            <button
              type="button"
              className="top-bar-nav-link"
              onClick={() => navigate('/')}
            >
              Home
            </button>
            <button
              type="button"
              className="top-bar-nav-link"
              onClick={() => navigate('/skincarecabinet')}
            >
              Cabinet
            </button>
            <button
              type="button"
              className="top-bar-nav-link"
              onClick={handleActiveIngredients}
            >
              Active Leveling
            </button>
          </nav>
        </div>
        {greeting && (
          <div className="top-bar-greeting">
            <Typography variant="body-small-bold" className="top-bar-greeting-message">
              {greeting.message}
            </Typography>
            <Typography variant="body-small" className="top-bar-greeting-date">
              {greeting.date}
            </Typography>
          </div>
        )}
        <div className="top-bar-right">
          {isAuthenticated ? (
            <>
              {/* Desktop user section - authenticated */}
              <div className="user-section">
                <div 
                  ref={desktopUserAvatarRef}
                  className="user-badge user-avatar-clickable"
                  onClick={handleDesktopUserAvatarClick}
                >
                  <Typography variant="body-small">{userInitials}</Typography>
                </div>
                <Popover
                  options={userPopoverOptions}
                  isOpen={isDesktopUserPopoverOpen}
                  onClose={() => setIsDesktopUserPopoverOpen(false)}
                  position="bottom"
                  align="end"
                  triggerRef={desktopUserAvatarRef}
                />
              </div>
              
              {/* Mobile user section - authenticated */}
              <div className="mobile-auth">
                <div className="mobile-user-section">
                  <div 
                    ref={mobileUserAvatarRef}
                    className="mobile-user-badge user-avatar-clickable"
                    onClick={handleMobileUserAvatarClick}
                  >
                    <Typography variant="body-small">{userInitials}</Typography>
                  </div>
                  <Popover
                    options={userPopoverOptions}
                    isOpen={isMobileUserPopoverOpen}
                    onClose={() => setIsMobileUserPopoverOpen(false)}
                    position="bottom"
                    align="end"
                    triggerRef={mobileUserAvatarRef}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Desktop auth buttons - unauthenticated */}
              <div className="user-section auth-buttons">
                <Button variant="text" onClick={login}>
                  Login
                </Button>
                <Button variant="primary" onClick={signup}>
                  Sign Up
                </Button>
              </div>
              
              {/* Mobile auth buttons - unauthenticated */}
              <div className="mobile-auth mobile-auth-buttons">
                <Button variant="text" onClick={login} className="mobile-login-btn">
                  Login
                </Button>
                <Button variant="primary" onClick={signup} className="mobile-signup-btn">
                  Sign Up
                </Button>
              </div>
            </>
          )}
          
          {/* Mobile menu button */}
          <div className="mobile-menu-container">
            <MobileMenuButton onClick={handleMobileMenuToggle} />
          </div>
        </div>
        </div>
      </section>
      
      <MobileMenu 
        isOpen={isMobileMenuOpen} 
        onClose={handleMobileMenuClose}
        onActiveLeveling={handleActiveIngredients}
      />
      
      {/* Active Ingredients Dialog */}
      <ResponsiveDialog
        type="activeIngredients"
        isOpen={isActiveIngredientsDialogOpen}
        onClose={() => setIsActiveIngredientsDialogOpen(false)}
        onActiveClick={handleActiveClick}
      />
      
      {/* Active Detail Dialog */}
      <ResponsiveDialog
        type="activeDetail"
        isOpen={isActiveDetailDialogOpen}
        onClose={handleCloseActiveDetail}
        onActiveDetailBack={handleBackToActiveIngredients}
        activeId={selectedActiveId ?? ''}
      />
    </>
  );
};

export default Topbar;